import React, { useState } from 'react';
import { CheckCircle2, Loader2, Plus, Trash2 } from 'lucide-react';

// Lead relay: 1Dash inbound webhook (server-side) -> BATS. Browser->1Dash hook is
// allowed (no CORS); the 1Dash workflow posts the full lead server-side to BATS v1
// (no CORS there). 1Dash External Tracking also captures this submit for attribution.
const BRIDGE_URL = 'https://services.leadconnectorhq.com/hooks/IExKC1X6hwJ1fw7H9LRY/webhook-trigger/ddb7fae2-17a0-492b-a068-7565b9899d31';
const BOAT_TYPES = ['Center Console', 'Cabin Cruiser', 'Bowrider', 'Sailboat', 'Pontoon Boat', 'Yacht', 'Catamaran', 'Personal Watercraft (Jet Ski)', 'Other Boat'];
const TRAILER_OPTS = ['On its own trailer', 'No trailer (needs transport trailer)'];
type Vehicle = {
  type: string;
  year: string;
  make: string;
  length: string;
  trailer: string;
};
const emptyVehicle = (): Vehicle => ({
  type: '',
  year: '',
  make: '',
  length: '',
  trailer: 'On its own trailer'
});
function getGclid(): string {
  try {
    const m = new RegExp('[?&]gclid=([^&]*)').exec(window.location.search);
    if (m && m[1]) {
      const g = decodeURIComponent(m[1].replace(/\+/g, ' '));
      try {
        localStorage.setItem('lj_gclid', g);
      } catch (e) {}
      return g;
    }
    try {
      return localStorage.getItem('lj_gclid') || '';
    } catch (e) {
      return '';
    }
  } catch (e) {
    return '';
  }
}
export function LeadForm({
  title = 'Get Your Free Boat Transport Quote',
  source = 'Boat PPC Landing'
}: {
  title?: string;
  source?: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    pickup: '',
    delivery: '',
    shipDate: ''
  });
  const [vehicles, setVehicles] = useState<Vehicle[]>([emptyVehicle()]);
  const update = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({
    ...form,
    [field]: e.target.value
  });
  const updateVehicle = (idx: number, field: keyof Vehicle, value: string) => {
    setVehicles(prev => prev.map((v, i) => i === idx ? {
      ...v,
      [field]: value
    } : v));
  };
  const addVehicle = () => setVehicles(prev => prev.length >= 5 ? prev : [...prev, emptyVehicle()]);
  const removeVehicle = (idx: number) => setVehicles(prev => prev.filter((_, i) => i !== idx));
  const describeVehicle = (v: Vehicle) => [v.year, v.make, v.length ? `${v.length}ft` : '', v.type ? `(${v.type})` : '', v.trailer].filter(Boolean).join(' ').trim();

  // Computed each render so the hidden mirror inputs always reflect current state
  // at the exact moment 1Dash's tracker reads the form on submit.
  const allDesc = vehicles.map(describeVehicle).filter(Boolean).join(' | ');
  const comment = ('Boats: ' + allDesc + ' | Pickup ' + form.pickup + ' to Delivery ' + form.delivery + ' | Ship ' + form.shipDate).slice(0, 480);
  const gclid = getGclid();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const first = vehicles[0] || emptyVehicle();
    // Split full name into first/last for BATS.
    const parts = form.fullName.trim().split(/\s+/);
    const firstName = parts.shift() || form.fullName;
    const lastName = parts.join(' ');
    // BATS needs mm/dd/yyyy; the date input gives yyyy-mm-dd.
    let shipDate = form.shipDate;
    const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(shipDate || '');
    if (dm) shipDate = dm[2] + '/' + dm[3] + '/' + dm[1];
    const thisYear = String(new Date().getFullYear());
    // BATS-shaped payload -> 1Dash inbound webhook -> 1Dash workflow -> BATS. All required fields filled.
    const payload = {
      first_name: firstName,
      last_name: lastName,
      email: form.email,
      phone: form.phone,
      ship_date: shipDate,
      transport_type: 1,
      origin_postal_code: form.pickup,
      destination_postal_code: form.delivery,
      vehicle_type: 'Boat',
      vehicle_make: first.make || first.type || 'Boat',
      vehicle_model: [first.make, first.length ? `${first.length}ft` : ''].filter(Boolean).join(' ') || first.type || 'Boat',
      vehicle_model_year: first.year || thisYear,
      vehicle_inop: 0,
      comment_from_shipper: comment,
      // extras for 1Dash side / reference
      full_name: form.fullName,
      source,
      lead_type: 'Boat',
      vehicle_details: allDesc,
      vehicle_count: String(vehicles.length),
      trailer_status: first.trailer,
      gclid: gclid
    };
    // Server-side relay handles BATS + 1Dash. Success is independent of this call
    // (1Dash External Tracking already captured the submit natively).
    try {
      await fetch(BRIDGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
        keepalive: true
      });
    } catch (err) {
      /* relay optional for the UX; lead is also in 1Dash via external tracking */
    }
    setIsSubmitting(false);
    setIsSuccess(true);
  };
  if (isSuccess) {
    return <div className="bg-white p-8 rounded-xl shadow-xl border border-slate-100 flex flex-col items-center justify-center text-center h-full min-h-[400px]" data-id="element-178">
        <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" data-id="element-179" />
        <h3 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-180">Quote Request Received!</h3>
        <p className="text-slate-600 mb-6" data-id="element-181">
          Thank you. One of our boat transport specialists will contact you shortly with your free, no-obligation quote.
        </p>
        <button onClick={() => {
        setIsSuccess(false);
        setForm({
          fullName: '',
          phone: '',
          email: '',
          pickup: '',
          delivery: '',
          shipDate: ''
        });
        setVehicles([emptyVehicle()]);
      }} className="text-brand-navy font-semibold hover:text-brand-red transition-colors" data-id="element-182">
          Submit another request
        </button>
      </div>;
  }
  const inputCls = 'w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-brand-navy focus:border-brand-navy outline-none transition-all';
  const labelCls = 'block text-sm font-semibold text-slate-700 mb-1';
  return <div className="bg-white p-6 md:p-8 rounded-xl shadow-xl border border-slate-100 w-full max-w-lg mx-auto" id="quote-form" data-id="element-183">
      <h3 className="text-2xl font-bold text-brand-navy mb-2 text-center" data-id="element-184">{title}</h3>
      <p className="text-sm text-slate-500 text-center mb-6 font-medium" data-id="element-185">No upfront payment • 100% free quote</p>

      <form onSubmit={handleSubmit} className="space-y-4" data-form-name="Boat Transport Quote" data-id="element-186">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-187">
          <div data-id="element-188">
            <label htmlFor="fullName" className={labelCls} data-id="element-189">Full Name</label>
            <input required value={form.fullName} onChange={update('fullName')} type="text" id="fullName" name="full_name" className={inputCls} placeholder="John Doe" data-id="element-190" />
          </div>
          <div data-id="element-191">
            <label htmlFor="phone" className={labelCls} data-id="element-192">Phone Number</label>
            <input required value={form.phone} onChange={update('phone')} type="tel" id="phone" name="phone" className={inputCls} placeholder="(555) 123-4567" data-id="element-193" />
          </div>
        </div>

        <div data-id="element-194">
          <label htmlFor="email" className={labelCls} data-id="element-195">Email Address</label>
          <input required value={form.email} onChange={update('email')} type="email" id="email" name="email" className={inputCls} placeholder="john@example.com" data-id="element-196" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-197">
          <div data-id="element-198">
            <label htmlFor="pickup" className={labelCls} data-id="element-199">Pickup ZIP Code</label>
            <input required value={form.pickup} onChange={update('pickup')} type="text" inputMode="numeric" id="pickup" name="origin_zip" className={inputCls} placeholder="e.g. 33301" data-id="element-200" />
          </div>
          <div data-id="element-201">
            <label htmlFor="delivery" className={labelCls} data-id="element-202">Delivery ZIP Code</label>
            <input required value={form.delivery} onChange={update('delivery')} type="text" inputMode="numeric" id="delivery" name="destination_zip" className={inputCls} placeholder="e.g. 89101" data-id="element-203" />
          </div>
        </div>

        {/* Dynamic boat rows */}
        <div className="space-y-4 pt-1" data-id="element-204">
          <label className={labelCls} data-id="element-205">Your Boat(s)</label>
          {vehicles.map((v, idx) => <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-id="element-206">
              <div className="flex items-center justify-between" data-id="element-207">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-208">Boat #{idx + 1}</span>
                {vehicles.length > 1 && <button type="button" onClick={() => removeVehicle(idx)} className="text-slate-400 hover:text-brand-red transition-colors" aria-label="Remove boat" data-id="element-209">
                    <Trash2 className="w-4 h-4" data-id="element-210" />
                  </button>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-211">
                <select required value={v.type} onChange={e => updateVehicle(idx, 'type', e.target.value)} className={inputCls} data-id="element-212">
                  <option value="" disabled data-id="element-213">Boat Type...</option>
                  {BOAT_TYPES.map(t => <option key={t} value={t} data-id="element-214">{t}</option>)}
                </select>
                <select value={v.trailer} onChange={e => updateVehicle(idx, 'trailer', e.target.value)} className={inputCls} data-id="element-215">
                  {TRAILER_OPTS.map(t => <option key={t} value={t} data-id="element-216">{t}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3" data-id="element-217">
                <input value={v.year} onChange={e => updateVehicle(idx, 'year', e.target.value)} type="text" inputMode="numeric" className={inputCls} placeholder="Year" data-id="element-218" />
                <input value={v.make} onChange={e => updateVehicle(idx, 'make', e.target.value)} type="text" className={inputCls} placeholder="Make/Builder" data-id="element-219" />
                <input value={v.length} onChange={e => updateVehicle(idx, 'length', e.target.value)} type="text" inputMode="numeric" className={inputCls} placeholder="Length (ft)" data-id="element-220" />
              </div>
            </div>)}
          {vehicles.length < 5 && <button type="button" onClick={addVehicle} className="flex items-center text-sm font-semibold text-brand-navy hover:text-brand-red transition-colors" data-id="element-221">
              <Plus className="w-4 h-4 mr-1" data-id="element-222" /> Add another boat
            </button>}
        </div>

        <div data-id="element-223">
          <label htmlFor="shipDate" className={labelCls} data-id="element-224">Preferred Ship Date</label>
          <input required value={form.shipDate} onChange={update('shipDate')} type="date" id="shipDate" name="ship_date" className={inputCls + ' text-slate-700'} data-id="element-225" />
        </div>

        {/* Hidden mirror fields captured by 1Dash External Tracking */}
        <input type="hidden" name="source" value={source} data-id="element-226" />
        <input type="hidden" name="lead_type" value="Boat" data-id="element-227" />
        <input type="hidden" name="vehicle_details" value={allDesc} data-id="element-228" />
        <input type="hidden" name="vehicle_count" value={String(vehicles.length)} data-id="element-229" />
        <input type="hidden" name="trailer_status" value={vehicles[0] ? vehicles[0].trailer : ''} data-id="element-230" />
        <input type="hidden" name="message" value={comment} data-id="element-231" />
        <input type="hidden" name="gclid" value={gclid} data-id="element-232" />

        <button type="submit" disabled={isSubmitting} data-submission-type="submit" className="w-full bg-brand-red hover:bg-brand-red-hover text-white font-bold py-4 px-6 rounded-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center text-lg mt-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-233">
          {isSubmitting ? <><Loader2 className="w-6 h-6 mr-2 animate-spin" data-id="element-234" />Processing...</> : 'Get My Free Quote'}
        </button>
        <p className="text-xs text-slate-500 text-center mt-3" data-id="element-235">By submitting, you agree to our terms. Your information is secure.</p>
      </form>
    </div>;
}